/**
 * `local-purge` — the ONE erase path for local per-user state (TASK 7.4 Step 4
 * / TASK 7.3 Step 3b).
 *
 * Two callers, one implementation, by contract:
 *
 *   1. **Sign-out** (`views/settings/Settings.js`) — AC 1.5 "sign-out erases
 *      local per-user state".
 *   2. **The global 403 revocation handler** (`api/api-client.ts`) — AC 2.5
 *      "first 403 -> best-effort purge of local content".
 *
 * `apps/web/test/local-purge.test.ts` asserts both call sites resolve to this
 * exact function object, so the two paths can never drift apart.
 *
 * ── ERASE, NOT HIDE ───────────────────────────────────────────────────────
 * Nothing here sets a "signed out" flag that a later read consults. Every
 * backend is genuinely emptied and the tests read the stores back to prove it:
 *
 *   | backend         | what is erased                                        |
 *   |-----------------|-------------------------------------------------------|
 *   | `localStorage`  | every key under the {@link LOCAL_KEY_PREFIX} namespace |
 *   | `sessionStorage`| same namespace (incl. the stashed `returnTo`)          |
 *   | IndexedDB       | every DB under {@link OWNED_DB_PREFIX} (audio blobs,   |
 *   |                 | catalog metadata, artwork, the telemetry buffer)       |
 *   | Cache Storage   | every cache under {@link USER_DATA_CACHE_PREFIX}       |
 *
 * ── WHAT IS DELIBERATELY **NOT** ERASED ───────────────────────────────────
 * The **app-shell precache** ({@link APP_SHELL_CACHE_PREFIX}). That cache holds
 * the application's own HTML/JS/CSS — code, not personal data. Wiping it on
 * every sign-out would force a full re-download for a user who signs out and
 * back in, and it protects nothing: it contains no user content. AC 2.5 scopes
 * the purge to "cached personal records, queue, and downloaded audio".
 *
 * ── BEST-EFFORT, PER-BACKEND ──────────────────────────────────────────────
 * A failure in one backend must never prevent the others from being purged
 * (AC 2.5 says "best-effort"). Each backend runs in its own `try`, failures are
 * collected into {@link PurgeReport.failures} AND logged (never swallowed
 * silently), and the function resolves rather than rejecting — a sign-out must
 * not be blocked by a wedged IndexedDB. It is idempotent: running it twice on
 * an already-clean profile reports zero failures and erases nothing further.
 *
 * ── NO SECRETS / NO PII IN LOGS ───────────────────────────────────────────
 * Only backend names, key names and error *types* are logged. Stored VALUES
 * (which may hold personal records) are never read, never logged.
 *
 * ── SEAM for TASK 10.1 (`offline-store.ts`) ───────────────────────────────
 * The database and cache names below are the single source of truth. TASK 10.1
 * must name its IndexedDB database `${OWNED_DB_PREFIX}<something>` and its
 * runtime caches `${USER_DATA_CACHE_PREFIX}<something>`, importing these
 * constants rather than re-declaring the strings — otherwise its downloads
 * survive a purge. {@link OFFLINE_DB_NAME} and {@link TELEMETRY_DB_NAME} are
 * the two concrete names already reserved.
 *
 * @module
 */

import { initialState, setState } from '../modules/app/state-store.js';

/** Namespace for every `localStorage` / `sessionStorage` key the app writes. */
export const LOCAL_KEY_PREFIX = 'sm:';

/** Every IndexedDB database the app owns starts with this. */
export const OWNED_DB_PREFIX = 'steamymusic-';

/** TASK 10.1's offline store (audio blobs + catalog metadata + artwork). */
export const OFFLINE_DB_NAME = `${OWNED_DB_PREFIX}offline`;

/** The client telemetry buffer (`lib/telemetry-client.ts`). */
export const TELEMETRY_DB_NAME = `${OWNED_DB_PREFIX}telemetry`;

/** Cache Storage entries holding USER data — purged. */
export const USER_DATA_CACHE_PREFIX = 'steamymusic-user-';

/** Cache Storage entry holding APP CODE — retained (see the module header). */
export const APP_SHELL_CACHE_PREFIX = 'steamymusic-shell-';

/*
 * The three CONCRETE cache names, reserved here beside the database names for
 * the same reason (TASK 10.1). They live in this module rather than in
 * `lib/offline-store.ts` because BOTH sides need them and only one side can
 * import the other: `sw/service-worker.ts` runs in a ServiceWorkerGlobalScope
 * with no DOM, and `offline-store.ts` transitively reaches the api-client. This
 * module imports nothing but the store, so both can depend on it — and the
 * purge test asserts these exact strings, so a rename cannot silently orphan a
 * cache full of user audio.
 */

/** Finished audio bodies the SW serves for `/api/stream/*` — USER data. */
export const AUDIO_CACHE_NAME = `${USER_DATA_CACHE_PREFIX}audio`;
/** Finished cover art the SW serves for `/api/art/*` — USER data. */
export const ART_CACHE_NAME = `${USER_DATA_CACHE_PREFIX}art`;
/**
 * Build id baked into {@link APP_SHELL_CACHE_NAME}, injected by Vite `define`
 * (see `apps/web/vite.config.ts`).
 *
 * ── WHY IT IS NOT A HAND-BUMPED `v1` (Dev W6) ─────────────────────────────
 * It was, and a constant that has to be remembered on every deploy is a
 * constant that is never bumped. `sw/service-worker.ts#handleShellAsset` writes
 * every hashed `/assets/*` chunk into this cache and nothing removes an ENTRY;
 * `activate()` only ever deletes whole caches under the prefix. With one
 * permanent name that meant every deploy orphaned the previous chunk set
 * forever and the shell cache grew without bound — while the worker's header
 * described a rotation that could not fire. A build-time value makes each
 * deploy a genuinely new cache, which is the thing that makes eviction real.
 *
 * The fallback covers `pnpm dev` and the test lane, where nothing is defined.
 */
declare const __APP_BUILD_ID__: string | undefined;

export const APP_SHELL_BUILD_ID: string =
  typeof __APP_BUILD_ID__ === 'string' && __APP_BUILD_ID__ !== '' ? __APP_BUILD_ID__ : 'dev';

/**
 * The app-shell precache — APP CODE, RETAINED across a purge. The build-id
 * suffix is what lets `sw/service-worker.ts` evict a superseded shell.
 */
export const APP_SHELL_CACHE_NAME = `${APP_SHELL_CACHE_PREFIX}${APP_SHELL_BUILD_ID}`;

/**
 * Databases deleted even when the runtime cannot enumerate them
 * (`indexedDB.databases()` is unimplemented in Firefox and in jsdom).
 */
export const KNOWN_DB_NAMES: readonly string[] = [OFFLINE_DB_NAME, TELEMETRY_DB_NAME];

/** How long to wait for a `blocked` IndexedDB delete before giving up. */
export const IDB_DELETE_TIMEOUT_MS = 3_000;

/** One backend's failure — surfaced to the caller AND logged. */
export interface PurgeFailure {
  /** `localStorage` | `sessionStorage` | `indexedDB:<name>` | `cacheStorage:<name>` */
  readonly backend: string;
  /** Error type/name only — never a stored value. */
  readonly reason: string;
}

export interface PurgeReport {
  /** Identifiers of everything actually erased (keys, DB names, cache names). */
  readonly purged: readonly string[];
  /** Non-fatal per-backend failures; the other backends still ran. */
  readonly failures: readonly PurgeFailure[];
}

/** Minimal shape of the storage APIs, so tests can inject fakes. */
export interface PurgeEnvironment {
  readonly localStorage?: Storage | undefined;
  readonly sessionStorage?: Storage | undefined;
  readonly indexedDB?: IDBFactory | undefined;
  readonly caches?: CacheStorage | undefined;
  /** Diagnostic sink; defaults to `console.warn`. */
  readonly warn?: (message: string, detail: Record<string, string>) => void;
  readonly timeoutMs?: number;
}

/** Error name (never `.message`, which could echo a stored value). */
function reasonOf(error: unknown): string {
  if (error instanceof Error) return error.name === '' ? 'Error' : error.name;
  return typeof error;
}

function purgeStorage(
  store: Storage | undefined,
  label: string,
  purged: string[],
  failures: PurgeFailure[],
): void {
  if (store === undefined || store === null) return;
  try {
    const doomed: string[] = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key !== null && key.startsWith(LOCAL_KEY_PREFIX)) doomed.push(key);
    }
    for (const key of doomed) {
      store.removeItem(key);
      purged.push(`${label}:${key}`);
    }
  } catch (error) {
    failures.push({ backend: label, reason: reasonOf(error) });
  }
}

/**
 * Deletes one IndexedDB database. Resolves (never rejects) so a wedged
 * database cannot block sign-out; `blocked` is treated as a timed failure
 * because another tab is holding the DB open — the delete completes once that
 * tab closes, and the next purge reports it clean.
 */
function deleteDatabase(
  factory: IDBFactory,
  name: string,
  timeoutMs: number,
): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    let settled = false;
    const finish = (failure: string | null): void => {
      if (settled) return;
      settled = true;
      resolve(failure);
    };
    let request: IDBOpenDBRequest;
    try {
      request = factory.deleteDatabase(name);
    } catch (error) {
      finish(reasonOf(error));
      return;
    }
    request.addEventListener('success', () => {
      finish(null);
    });
    request.addEventListener('error', () => {
      finish(reasonOf(request.error));
    });
    request.addEventListener('blocked', () => {
      finish('BlockedError');
    });
    setTimeout(() => {
      finish('TimeoutError');
    }, timeoutMs);
  });
}

async function purgeIndexedDb(
  factory: IDBFactory | undefined,
  timeoutMs: number,
  purged: string[],
  failures: PurgeFailure[],
): Promise<void> {
  if (factory === undefined || factory === null) return;

  const names = new Set<string>(KNOWN_DB_NAMES);
  try {
    // Not implemented in Firefox/jsdom — the KNOWN_DB_NAMES floor covers those.
    const enumerate = factory.databases?.bind(factory);
    if (typeof enumerate === 'function') {
      for (const info of await enumerate()) {
        if (typeof info.name === 'string' && info.name.startsWith(OWNED_DB_PREFIX)) {
          names.add(info.name);
        }
      }
    }
  } catch (error) {
    failures.push({ backend: 'indexedDB:databases()', reason: reasonOf(error) });
  }

  // Sequential, not Promise.all: a `blocked` delete holds a connection slot and
  // parallel deletes make the blocked-vs-timeout signal ambiguous.
  for (const name of names) {
    const failure = await deleteDatabase(factory, name, timeoutMs);
    if (failure === null) purged.push(`indexedDB:${name}`);
    else failures.push({ backend: `indexedDB:${name}`, reason: failure });
  }
}

/** True for a cache holding user data (never the app-shell precache). */
export function isUserDataCache(name: string): boolean {
  if (name.startsWith(APP_SHELL_CACHE_PREFIX)) return false;
  return name.startsWith(USER_DATA_CACHE_PREFIX);
}

async function purgeCaches(
  cacheStorage: CacheStorage | undefined,
  purged: string[],
  failures: PurgeFailure[],
): Promise<void> {
  if (cacheStorage === undefined || cacheStorage === null) return;
  let names: string[];
  try {
    names = await cacheStorage.keys();
  } catch (error) {
    failures.push({ backend: 'cacheStorage:keys()', reason: reasonOf(error) });
    return;
  }
  for (const name of names) {
    if (!isUserDataCache(name)) continue;
    try {
      await cacheStorage.delete(name);
      purged.push(`cacheStorage:${name}`);
    } catch (error) {
      failures.push({ backend: `cacheStorage:${name}`, reason: reasonOf(error) });
    }
  }
}

/**
 * Erases every persistent per-user store. Best-effort and idempotent; resolves
 * with a report instead of rejecting.
 */
export async function localPurge(env: PurgeEnvironment = {}): Promise<PurgeReport> {
  const scope = globalThis as unknown as {
    localStorage?: Storage;
    sessionStorage?: Storage;
    indexedDB?: IDBFactory;
    caches?: CacheStorage;
  };
  const {
    localStorage: local = scope.localStorage,
    sessionStorage: session = scope.sessionStorage,
    indexedDB: idb = scope.indexedDB,
    caches: cacheStorage = scope.caches,
    timeoutMs = IDB_DELETE_TIMEOUT_MS,
    warn = (message, detail) => {
      console.warn(message, detail);
    },
  } = env;

  const purged: string[] = [];
  const failures: PurgeFailure[] = [];

  purgeStorage(local, 'localStorage', purged, failures);
  purgeStorage(session, 'sessionStorage', purged, failures);
  await purgeIndexedDb(idb, timeoutMs, purged, failures);
  await purgeCaches(cacheStorage, purged, failures);

  // Never swallowed: a partial purge is a security-relevant event.
  for (const failure of failures) {
    warn('local-purge: backend failed (continuing with the others)', {
      backend: failure.backend,
      reason: failure.reason,
    });
  }

  return { purged, failures };
}

/**
 * Work that must FINISH before the erase begins — see {@link setPrePurgeQuiesce}.
 *
 * @type {() => Promise<unknown>}
 */
let prePurgeQuiesce: () => Promise<unknown> = () => Promise.resolve();

/**
 * Installs the routine that stops every writer BEFORE {@link purgeLocalState}
 * erases anything. Returns the previous one, so a caller can restore it
 * (the composition root's `dispose` does).
 *
 * ── WHY THIS LIVES HERE AND NOT AT THE CALL SITES (Security C1) ───────────
 * Erasing storage does not stop code that is writing to it, and both of this
 * module's callers leave the page running: sign-out is `await purge();
 * go('#/signin')`, and `go` is a HASH NAVIGATION, not a reload. The download
 * manager is constructed once at boot and torn down only by `app.dispose()`,
 * which sign-out never calls — so its in-flight transfer sails straight through
 * the purge. Both stores then RE-CREATE THEMSELVES on the next write
 * (`indexedDB.open` on a deleted database builds it; `caches.open` on a deleted
 * cache builds it), so user A's next 1 MiB flush restores the database, the
 * audio cache, A's complete audio body and A's catalog metadata — and user B,
 * signing in on the same device, is served A's albums and A's bytes.
 *
 * The hook is HERE rather than at the two call sites for the same reason the
 * erase itself is: `apps/web/test/local-purge.test.ts` asserts both callers
 * resolve to this exact function object, so anything installed here is
 * guaranteed to run for sign-out AND for the AC 2.5 revocation path, and the
 * two can never drift apart. A quiesce that had to be remembered at each call
 * site is a quiesce that will eventually be forgotten at one of them.
 *
 * @param next the new quiesce routine
 * @returns the routine it replaced
 */
export function setPrePurgeQuiesce(next: () => Promise<unknown>): () => Promise<unknown> {
  const previous = prePurgeQuiesce;
  prePurgeQuiesce = next;
  return previous;
}

/**
 * THE shared erase routine both sign-out (TASK 7.4) and the global 403
 * revocation handler (TASK 7.3) invoke: every writer quiesced via
 * {@link setPrePurgeQuiesce}, then persistent stores via {@link localPurge},
 * plus the in-memory store's personal slices (session, catalog,
 * queue/playback) reset to their zero values.
 *
 * The route slice is preserved deliberately — the caller decides where to send
 * the user next, and clobbering `state.route` here would make the shell
 * re-render a route the router has not navigated to.
 */
export async function purgeLocalState(env: PurgeEnvironment = {}): Promise<PurgeReport> {
  /*
   * FIRST, and awaited. A purge that races a writer is not a purge — see
   * `setPrePurgeQuiesce`. Best-effort like everything else here: a quiesce
   * that throws must not leave the user's data un-erased, so the erase
   * proceeds either way and the failure is reported through the same warn
   * channel as a failed backend.
   */
  try {
    await prePurgeQuiesce();
  } catch (error) {
    (env.warn ?? ((message, detail) => console.warn(message, detail)))(
      'local-purge: pre-purge quiesce failed (erasing anyway)',
      { reason: reasonOf(error) },
    );
  }
  const report = await localPurge(env);
  const zero = initialState();
  setState({ session: zero.session, catalog: zero.catalog, playback: zero.playback });
  return report;
}
